import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Info, Loader } from 'lucide-react'
import { useEffect, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { CREATE_COMPANY_MUTATION } from '@/components/layout/breadcrumbs/company-switcher/gql'
import { USER_COMPANIES_QUERY } from '@/components/root/gql'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type {
  CreateCompanyMutation,
  CreateCompanyMutationVariables,
} from '@/components/layout/breadcrumbs/company-switcher/gql.types.gen'

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: 'Company name is required' })
    .regex(/^[\p{L}\p{N}\p{P}\p{Z}]+$/u, {
      message: 'Company name contains non-alphanumeric unicode characters',
    }),
})

type Props = {
  isOpen: boolean
  onClose: () => void
  onCreated: (idCompany: string) => void
}

export const CreateCompanySheet = ({ isOpen, onClose, onCreated }: Props) => {
  const [isPending, startTransition] = useTransition()

  const [createCompany] = useMutation<
    CreateCompanyMutation,
    CreateCompanyMutationVariables
  >(CREATE_COMPANY_MUTATION)

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: '' },
    mode: 'onChange',
  })

  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = form

  useEffect(() => {
    if (isOpen) {
      reset({ name: '' })
    }
  }, [isOpen, reset])

  const onSubmit = async (data: z.infer<typeof schema>) => {
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        const { data: result } = await createCompany({
          variables: { name: data.name },
          refetchQueries: [{ query: USER_COMPANIES_QUERY }],
          awaitRefetchQueries: true,
        })
        const idCompany = result?.createCompany ?? ''
        if (!idCompany) {
          throw new Error('Missing company id')
        }
        toast(
          <ToasterContent variant="success">
            Company created successfully
          </ToasterContent>
        )
        onCreated(idCompany)
        onClose()
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to create company
          </ToasterContent>
        )
      }
    })
  }

  return (
    <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="sm:max-w-[600px]">
        <SheetHeader>
          <SheetTitle>Create new company</SheetTitle>
        </SheetHeader>
        <div className="px-4 overflow-y-auto w-full pb-2 space-y-6">
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="off"
                        aria-label="Company name"
                        placeholder="My company"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" hidden />
            </form>
          </Form>
        </div>
        <SheetFooter className="sticky gap-4">
          <Alert>
            <Info />
            <AlertTitle>Billing details required next</AlertTitle>
            <AlertDescription>
              <p>
                After creating the company, you&apos;ll be asked to provide
                billing details and a payment method before you can start using
                it.
              </p>
            </AlertDescription>
          </Alert>
          <Button
            disabled={isPending || !isValid}
            onClick={handleSubmit(onSubmit)}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Create company
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
